import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { AuthService } from '../../features/auth/data/services/auth.service';
import { Store } from '@ngrx/store';
import {
  loginSuccess,
  logoutButtonClicked,
} from '../../features/auth/state/auth.actions';
import { User } from '../../features/auth/domain/models/user';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authTokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  if (req.headers.has('X-Skip-Interceptor')) {
    const headers = req.headers.delete('X-Skip-Interceptor');
    return next(req.clone({ headers }));
  }

  const authService = inject(AuthService);
  const store = inject(Store);
  const toastr = inject(ToastrService);
  const translate = inject(TranslateService);

  return authService.getToken().pipe(
    switchMap((token) => {
      const authReq = token
        ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : req;
      const finalReq = authReq.clone({ withCredentials: req.withCredentials });

      return next(finalReq).pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 401) {
            return handle401Error(
              req,
              next,
              authService,
              store,
              toastr,
              translate,
              err,
            );
          }

          return throwError(() => err);
        }),
      );
    }),
  );
};

function handle401Error(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  store: Store,
  toastr: ToastrService,
  translate: TranslateService,
  originalErr: HttpErrorResponse,
): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((res: { token: string; user: User }) => {
        isRefreshing = false;
        refreshTokenSubject.next(res.token);
        store.dispatch(loginSuccess(res));
        const retryReq = req.clone({
          setHeaders: { Authorization: `Bearer ${res.token}` },
        });
        return next(retryReq);
      }),
      catchError(() => {
        isRefreshing = false;
        refreshTokenSubject.next(null);
        store.dispatch(logoutButtonClicked());
        toastr.error(
          translate.instant('toastr.sessionExpired'),
          translate.instant('toastr.unauthorized'),
        );
        return throwError(() => originalErr);
      }),
    );
  }

  return refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) => {
      const retryReq = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
      return next(retryReq);
    }),
  );
}
